import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as FlightSearchService from '_services/flight-search-service';

/**
 * Search for flights
 * @param {Object} params - Search parameters
 * @param {string} params.iata - Airport IATA code (required)
 * @param {string} params.date - Date in YYYY-MM-DD format (required)
 * @param {string} params.searchTerm - General search term (optional)
 * @param {string} params.type - 'all', 'arrival', or 'departure' (optional)
 * @param {string} params.flightNumber - Specific flight number (optional)
 * @param {string} params.origin - Origin airport code (optional)
 * @param {string} params.destination - Destination airport code (optional)
 */
export const searchFlights = createAsyncThunk(
    'flightSearch/searchFlights',
    async (params) => {
        return FlightSearchService.searchFlights(params);
    }
);

const initialState = {
    flights: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    lastSearchParams: null,
    searchTime: null,
};

const flightSearchSlice = createSlice({
    name: 'flightSearch',
    initialState,
    reducers: {
        // Reset the search results
        resetSearch: (state) => {
            state.flights = [];
            state.status = 'idle';
            state.error = null;
            state.lastSearchParams = null;
            state.searchTime = null;
        },
        // Clear only the results but keep the status
        clearResults: (state) => {
            state.flights = [];
        },
    },
    extraReducers: {
        [searchFlights.pending]: (state, action) => {
            state.status = 'loading';
            state.error = null;
            state.lastSearchParams = action.meta.arg;
        },
        [searchFlights.fulfilled]: (state, action) => {
            state.status = 'succeeded';
            state.flights = action.payload || [];
            state.searchTime = new Date().toISOString();
        },
        [searchFlights.rejected]: (state, action) => {
            state.status = 'failed';
            state.flights = [];
            state.error = action.error.message;
        },
    },
});

// Export actions
export const { resetSearch, clearResults } = flightSearchSlice.actions;

// Export reducer
export default flightSearchSlice.reducer;

// Selectors
export const selectFlightSearchResults = (state) => state.flightSearch.flights;
export const selectFlightSearchStatus = (state) => state.flightSearch.status;
export const selectFlightSearchError = (state) => state.flightSearch.error;
export const selectLastSearchParams = (state) => state.flightSearch.lastSearchParams;
export const selectSearchTime = (state) => state.flightSearch.searchTime;

// Derived selectors
export const selectFlightSearchCount = (state) => state.flightSearch.flights.length;
export const selectIsSearching = (state) => state.flightSearch.status === 'loading';
export const selectHasResults = (state) =>
    state.flightSearch.status === 'succeeded' && state.flightSearch.flights.length > 0;
export const selectHasError = (state) => state.flightSearch.status === 'failed';


